import {
  IconExclamationTriangleOutline,
  IconFileWithItemsOutline,
  IconPlus,
} from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { TFile } from 'components/Signup/hooks/useFileUploadReducer';
import { TDocumentFeedbackItem } from 'lendingApi/lendingApi.types';
import { translateDocsFeedbackType } from './UploadedFiles.helper';
import styles from './UploadedFiles.module.css';

export type TFileStatus =
  | 'loading'
  | 'error'
  | 'success'
  | 'rejected'
  | 'overridden';

export const UploadedFiles = ({
  files,
  removeFile,
}: {
  files: TFile[];
  removeFile: (id: string) => void;
}) => {
  const renderIcon = (status: TFileStatus) => {
    if (status === 'loading') {
      return <Spinner dataTestId="loading-icon" className={styles.spinner} />;
    }

    const uploadHasErrored = status === 'error' || status === 'rejected';
    const Icon = uploadHasErrored
      ? IconExclamationTriangleOutline
      : IconFileWithItemsOutline;

    return (
      <Icon
        data-testid={`${
          status === 'error'
            ? 'error'
            : status === 'rejected'
              ? 'reject'
              : 'success'
        }-icon`}
        width={20}
        height={20}
        className={clsx(styles.icon, {
          [styles.errorIcon]: status === 'error',
          [styles.rejectedIcon]: status === 'rejected',
          [styles.overriddenIcon]: status === 'overridden',
        })}
      />
    );
  };

  const renderFeedback = (
    docsFeedback: TDocumentFeedbackItem[],
    status: TFileStatus,
  ) => {
    const feedbackItems = docsFeedback
      .map((feedback) => ({
        id: feedback.id,
        content: translateDocsFeedbackType(feedback.feedback_type),
      }))
      .filter(({ content }) => content !== null);

    if (feedbackItems.length === 0) return null;

    if (feedbackItems.length === 1) {
      return (
        <p
          className={clsx(styles.errorMessage, styles.feedbackMessage, {
            [styles.overriddenMessage]: status === 'overridden',
          })}
        >
          {feedbackItems[0].content}
        </p>
      );
    }

    return (
      <ul className={styles.feedbackList}>
        {feedbackItems.map(({ id, content }) => (
          <li
            key={id}
            className={clsx(styles.errorMessage, styles.feedbackMessage, {
              [styles.overriddenMessage]: status === 'overridden',
            })}
          >
            {content}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {files.map((file) => {
        const hasError = file.status === 'error';
        const isRejected = file.status === 'rejected';
        const isOverridden = file.status === 'overridden';

        return (
          <div
            key={file.id}
            data-testid={`uploaded-file-${file.id}`}
            className={styles.uploadedFiles}
          >
            <div className={styles.fileNameContainer}>
              <div className={styles.iconContainer}>
                {renderIcon(file.status)}
              </div>
              <p
                className={clsx(styles.name, {
                  [styles.errored]: hasError,
                  [styles.rejected]: isRejected,
                  [styles.overridden]: isOverridden,
                })}
              >
                {file.fileName}
              </p>
              {hasError && (
                <Button
                  data-testid="remove-button"
                  size="small"
                  variant="tertiary"
                  onClick={() => removeFile(file.id)}
                >
                  <IconPlus
                    height={20}
                    width={20}
                    className={styles.removeIcon}
                  />
                </Button>
              )}
            </div>
            {file.errorReason && !isRejected && (
              <div className={styles.errorMessageContainer}>
                <p className={styles.errorMessage}>{file.errorReason}</p>
              </div>
            )}
            {!file.errorReason && isRejected && !file.docsFeedback?.length && (
              <div className={styles.errorMessageContainer}>
                <p
                  className={clsx(styles.errorMessage, styles.rejectedMessage)}
                >
                  {file.rejectReason}
                </p>
              </div>
            )}
            {file.docsFeedback && file.docsFeedback.length > 0 && (
              <div className={styles.errorMessageContainer}>
                {renderFeedback(file.docsFeedback, file.status)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
